import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { apiError } from '../../lib/moderationView';
import { CLASS_TEXT, CLASS_TONE, readinessNote, shareRows } from '../../lib/shadowView';
import { hubService, type ShadowItem, type ShadowReadinessData, type ShadowSurface } from '../../services/hub.service';

const NOTE_TONE = { gray: 'text-gray-500', amber: 'text-amber-700', green: 'text-green-700' } as const;
const SURFACE_TEXT = { dm: 'Messages (DM)', comment: 'Comments' } as const;

/**
 * Quality → "Hub vs Rasa" (spec infrastructure 2026-10-07-intent-shadow-comparison): while a v1 bot is imported in
 * `shadow`, every Rasa decision is silently re-classified by hub. Shows how often they agree per surface and lets the
 * owner judge the disagreements (each verdict becomes a gold label). The flip to live stays the owner's call.
 */
export function ShadowReadiness({ botId, system }: { botId: string; system: 'chatbot' | 'chatbot_v2' }) {
  const { getToken } = useAuth();
  const [days, setDays] = useState(14);
  const [data, setData] = useState<ShadowReadinessData | null>(null);
  const [items, setItems] = useState<{ rows: ShadowItem[]; total: number } | null>(null);
  const [pending, setPending] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setMsg(null);
    try {
      const r = await hubService.getShadowReadiness(botId, system, days, getToken());
      setData(r);
      setItems(r.state ? await hubService.listShadowItems(botId, system, { pending, limit: 50 }, getToken()) : null);
    } catch (e) {
      setMsg(apiError(e));
    }
  }, [botId, system, days, pending, getToken]);

  useEffect(() => {
    if (system === 'chatbot') void load();
  }, [load, system]);

  const judge = async (id: string, verdict: 'rasa' | 'hub' | 'neither') => {
    setMsg(null);
    try {
      await hubService.shadowVerdict(botId, system, id, verdict, getToken());
      await load();
    } catch (e) {
      setMsg(apiError(e));
    }
  };

  if (system !== 'chatbot') return null; // v1 bots only: Rasa runs there
  return (
    <section className="space-y-3 rounded border border-gray-200 p-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <h3 className="mr-2 font-medium">Hub vs Rasa</h3>
        {data?.state && (
          <>
            <span className="text-gray-600">Last</span>
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`rounded border px-2 py-0.5 ${days === d ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white'}`}
              >
                {d} days
              </button>
            ))}
            <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">import: {data.state}</span>
          </>
        )}
        <Link to="/intent-import" className="ml-auto text-blue-600 hover:underline">
          Platform → Intent import
        </Link>
      </div>
      {msg && <div className="text-sm text-red-600">{msg}</div>}

      {data && !data.state && (
        <div className="text-sm text-gray-500">Not imported — start a shadow import in Platform → Intent import to compare.</div>
      )}

      {data?.state && (
        <>
          {data.state !== 'shadow' && (
            <div className="text-xs text-gray-500">Comparison runs only in state shadow; numbers below are history.</div>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            {data.surfaces.map((s) => (
              <SurfaceCard key={s.surface} s={s} />
            ))}
          </div>

          <div className="text-sm">
            <div className="mb-1 flex items-center gap-3">
              <span className="font-medium">Disagreements</span>
              <label className="flex items-center gap-1 text-xs text-gray-600">
                <input type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} />
                not judged only
              </label>
              {items && <span className="text-xs text-gray-500">{items.total} total</span>}
            </div>
            {!items || items.rows.length === 0 ? (
              <div className="text-gray-500">Nothing to judge.</div>
            ) : (
              <ul className="space-y-1">
                {items.rows.map((it) => (
                  <li key={it.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 py-1">
                    <span className="min-w-0 flex-1">
                      {it.text}
                      <span className="ml-2 whitespace-nowrap text-xs text-gray-500">
                        {it.surface} · Rasa {it.rasaIntentId ? `#${it.rasaIntentId}` : '—'} · hub {hubChoice(it)}
                      </span>
                    </span>
                    {it.verdict ? (
                      <span className="text-xs text-gray-600">
                        {it.verdict === 'neither' ? 'neither right' : `${it.verdict} right`}
                        {it.verdictBy ? ` · ${it.verdictBy}` : ''}
                      </span>
                    ) : (
                      <span className="flex gap-1 text-xs">
                        <VerdictButton label="Rasa right" onClick={() => void judge(it.id, 'rasa')} />
                        <VerdictButton label="Hub right" onClick={() => void judge(it.id, 'hub')} />
                        <VerdictButton label="Neither" onClick={() => void judge(it.id, 'neither')} />
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function hubChoice(it: ShadowItem): string {
  const id = it.hubIntentId ? `#${it.hubIntentId}` : '';
  const what = [id, it.hubCode].filter(Boolean).join(' ') || '—';
  return it.hubScore != null ? `${what} (${Number(it.hubScore).toFixed(2)})` : what;
}

function SurfaceCard({ s }: { s: ShadowSurface }) {
  const rows = shareRows(s.counts);
  const note = readinessNote(s.n, s.counts);
  return (
    <div className="rounded border border-gray-200 p-3 text-sm">
      <div className="mb-1 flex justify-between">
        <span className="font-medium">{SURFACE_TEXT[s.surface]}</span>
        <span className="text-gray-500">
          n={s.n}
          {s.avgLatencyMs != null ? ` · ${s.avgLatencyMs} ms` : ''}
        </span>
      </div>
      <div className="mb-2 flex h-2 overflow-hidden rounded bg-gray-100">
        {rows.map((r) => (r.pct > 0 ? <div key={r.cls} className={CLASS_TONE[r.cls]} style={{ width: `${r.pct}%` }} /> : null))}
      </div>
      <ul className="space-y-0.5 text-xs">
        {rows.map((r) => (
          <li key={r.cls} className="flex justify-between">
            <span className="flex items-center gap-1">
              <span className={`inline-block h-2 w-2 rounded-sm ${CLASS_TONE[r.cls]}`} />
              {CLASS_TEXT[r.cls]}
            </span>
            <span className="text-gray-600">
              {r.n} · {r.pct}%
            </span>
          </li>
        ))}
      </ul>
      {s.hubOnPost && s.hubOnPost.yes + s.hubOnPost.no > 0 && (
        <div className="mt-1 text-xs text-gray-600">
          Hub's intent configured on the post: {s.hubOnPost.yes} yes · {s.hubOnPost.no} no
        </div>
      )}
      <div className={`mt-2 text-xs ${NOTE_TONE[note.tone]}`}>{note.text}</div>
    </div>
  );
}

function VerdictButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="rounded border border-gray-300 bg-white px-2 py-0.5 hover:bg-gray-50">
      {label}
    </button>
  );
}
